import React from "react";
import ReactDOM from "react-dom/client";
import { App } from "@/App";
import "@/styles.css";
import { diag, reportCapabilities, startHeartbeat, testMediaSource, testVideoFromMemory } from "@/lib/diag";

reportCapabilities();
startHeartbeat();
// Settles, per device, whether video can play from memory at all.
void testVideoFromMemory().then(() => testMediaSource());

// Registered conditionally — inert on browsers without Service Worker
// support (e.g. VIDAA), fully functional on Chromium-based kiosk devices.
// Registered relative to BASE_URL, not "/": under a subfolder deploy the
// worker lives at /signage/sw.js, and registering "/sw.js" would 404 — and
// even if it resolved, a worker served from the root would claim a scope
// wider than this app owns.
// The outcome is reported, not just logged. The TVs report that they support
// service workers and then never fetch sw.js at all, and a console warning on
// a screen nobody can open DevTools on is the same as no warning.
if ("serviceWorker" in navigator) {
  const url = `${import.meta.env.BASE_URL}sw.js`;
  // These TVs never settle this promise at all — no success, no failure, and
  // sw.js is never even fetched. Without a race the outcome is simply never
  // reported, which looks identical to "not tested".
  const hung = setTimeout(() => diag("sw_reg", { ok: false, err: "no-response" }), 12000);
  navigator.serviceWorker.register(url).then(
    (reg) => {
      clearTimeout(hung);
      diag("sw_reg", {
        ok: true,
        scope: reg.scope.replace(location.origin, ""),
        active: !!reg.active,
        controlled: !!navigator.serviceWorker.controller,
      });
      navigator.serviceWorker.ready
        .then((ready) => diag("sw_ready", { active: !!ready.active, controlled: !!navigator.serviceWorker.controller }))
        .catch(() => undefined);
    },
    (err) => {
      clearTimeout(hung);
      console.warn("SW registration failed:", err);
      diag("sw_reg", { ok: false, err: String((err as Error)?.name || err).slice(0, 60), msg: String((err as Error)?.message || "").slice(0, 80) });
    },
  );
} else {
  diag("sw_reg", { ok: false, err: "unsupported" });
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
